import { Checkbox, Tooltip, makeStyles } from "@fluentui/react-components";
import { getCategories } from "api";
import { ProgressBarIndeterminate } from "components/ProgressBar";
import { ICategory } from "models/ICategory";
import { ITag } from "models/ITag";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { setTagsForKey } from "../../redux/tags";
import { ICategoryTagTreeProps } from "./CategoryTagTree.types";

const useStyles = makeStyles({
  container: {
    overflowY: "auto",
    borderRadius: "8px",
    alignContent: "flex-start",
    padding: "10px",
  },
  categoryItem: {
    display: "flex",
    gap: "8px",
    padding: "4px 0",
  },
  subCategoryItem: {
    marginLeft: "24px",
    display: "flex",

    gap: "8px",
  },
  applyButton: {
    marginTop: "12px",
    width: "100%",
  },
  checkboxLabel: {
    cursor: "pointer",
    alignContent: "center",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "inline-block",
  },
});

export const CategoryTagTree = (props: ICategoryTagTreeProps): JSX.Element => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const {
    data: categories,
    isLoading,
    isError,
  } = useQuery(
    "categories",
    async () => {
      const categories = await getCategories(false);
      return categories;
    },
    { refetchOnWindowFocus: false },
  );

  const currentTags = useSelector(
    (state: RootState) => state.currentTagsSlice.tags[props.dictKey] || [],
  );

  const [tempSelectedTags, setTempSelectedTags] = useState<ITag[]>(currentTags);
  const [expandedCategories, setExpandedCategories] = useState<{
    [key: string]: boolean;
  }>({});

  useEffect((): void => {
    if (categories) {
      const initialState = categories.reduce(
        (acc: any, category: ICategory) => {
          acc[category.id] =
            category.title === "General" ||
            category.tags.some((tag) =>
              tempSelectedTags.some((selectedTag) => selectedTag.id === tag.id),
            );
          return acc;
        },
        {},
      );
      setExpandedCategories(initialState);
    }
  }, [categories, tempSelectedTags]);

  const tagIds = tempSelectedTags?.length
    ? tempSelectedTags.map((tag: ITag) => tag.id)
    : [];

  const toggleTagSelection = (tag: ITag) => {
    const updatedTags = tagIds.includes(tag.id)
      ? tempSelectedTags.filter((currentTag) => currentTag.id !== tag.id)
      : [...tempSelectedTags, tag];

    setTempSelectedTags(updatedTags);
    applyFilters(updatedTags);
  };

  const toggleCategoryExpansion = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const applyFilters = (updatedTags: ITag[]) => {
    dispatch(setTagsForKey({ key: props.dictKey, tags: updatedTags }));
    props.onDismiss();
  };

  if (isLoading) {
    return <ProgressBarIndeterminate />;
  }

  if (isError || !Array.isArray(categories)) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {categories.map((category: ICategory) => (
        <div key={category.id}>
          <div className={styles.categoryItem}>
            <Checkbox
              checked={
                category.tags.length > 0 &&
                category.tags.every((tag) => tagIds.includes(tag.id))
              }
              onChange={() => {
                const allSelected = category.tags.every((tag) =>
                  tagIds.includes(tag.id),
                );
                const updatedTags = allSelected
                  ? tempSelectedTags.filter(
                      (tag) => !category.tags.some((ct) => ct.id === tag.id),
                    )
                  : [
                      ...tempSelectedTags,
                      ...category.tags.filter(
                        (tag) => !tagIds.includes(tag.id),
                      ),
                    ];

                setTempSelectedTags(updatedTags);
                applyFilters(updatedTags);
              }}
            />
            <Tooltip
              content={
                <>
                  <strong>{category.title}</strong>
                  <br /> <br />
                  {category.description || "No description"}
                </>
              }
              relationship="label"
            >
              <span
                className={styles.checkboxLabel}
                onClick={() => toggleCategoryExpansion(category.id)}
              >
                {expandedCategories[category.id] ? "▼ " : "▶ "} {category.title}
              </span>
            </Tooltip>
          </div>

          {expandedCategories[category.id] &&
            category.tags.map((tag: ITag) => (
              <div key={tag.id} className={styles.subCategoryItem}>
                <div style={{ alignContent: "flex-start" }}>
                  <Checkbox
                    checked={tagIds.includes(tag.id)}
                    onChange={() => toggleTagSelection(tag)}
                  />
                </div>

                <Tooltip
                  content={
                    <>
                      <strong>{tag.name}</strong>
                      <br /> <br />
                      {tag.description || "No description"}
                    </>
                  }
                  relationship="label"
                >
                  <span
                    className={styles.checkboxLabel}
                    onClick={() => toggleTagSelection(tag)}
                  >
                    {tag.name}
                  </span>
                </Tooltip>
              </div>
            ))}
        </div>
      ))}
    </div>
  );
};
